"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAppManifestReferences = getAppManifestReferences;
const is_object_1 = __importDefault(require("./is-object"));
/**
 * Takes in an app manifest and returns all of the places that each page,
 * content item, and collection are refenced including:
 * - moduleIds: All the modules that reference the item directly
 * - tabIds: All the tabs that reference the item directly
 * - parentTabIds: For pages only. All the tabs where the page can be reached,
 *   sorted with the tab that includes the page with the least indirection
 *   first.
 *
 * NOTE: The logic in this function may seem somewhat torturous. There would be
 * more straightforward ways to gather these references, but this function is
 * designed first and foremost for performance. It has to walk through the
 * entire app manifest which ispotentially extremely large and it has to do it
 * client-side in environments that might be highly resource constrained.
 */
function getAppManifestReferences(_a) {
    var { manifest: { tabs, modules, pages } } = _a, options = __rest(_a, ["manifest"]);
    const referencesByModule = new Map();
    modules.forEach((module) => {
        referencesByModule.set(module.id, getReferences(module.config, options));
    });
    const linksByTab = new Map();
    const pageDepthsByTab = new Map();
    tabs.forEach((tab) => {
        // Create a map of tab ids to tab links
        if ((options.includePageReferences && isPageLink(tab.link)) ||
            (options.includeContentReferences && isContentLink(tab.link)) ||
            (options.includeCollectionReferences && isCollectionLink(tab.link))) {
            linksByTab.set(tab.id, tab.link);
        }
        // Create a map of tab ids to the depth of the pages in the tab
        const pageDepths = new Map();
        const link = linksByTab.get(tab.id);
        if ((link === null || link === void 0 ? void 0 : link.type) === "page") {
            pageDepths.set(link.pageId, 0);
            getPageDepths(link.pageId, pages, referencesByModule, pageDepths);
        }
        pageDepthsByTab.set(tab.id, pageDepths);
    });
    const result = {
        pageReferences: {},
        contentReferences: {},
        collectionReferences: {},
    };
    if (options.includePageReferences) {
        pages.forEach((page) => {
            const tabIds = getKeysWhere(linksByTab, (link) => link.type === "page" && link.pageId === page.id);
            const moduleIds = getKeysWhere(referencesByModule, (module) => module.pageIds.has(page.id));
            const parentTabs = [];
            pageDepthsByTab.forEach((pageDepths, tabId) => {
                const depth = pageDepths.get(page.id);
                if (depth !== undefined) {
                    parentTabs.push({ tabId, depth });
                }
            });
            const parentTabIds = parentTabs
                .sort((a, b) => a.depth - b.depth)
                .map(({ tabId }) => tabId);
            result.pageReferences[page.id] = { tabIds, moduleIds, parentTabIds };
        });
    }
    if (options.includeContentReferences) {
        // Build up a set of unique content ids
        const contentIds = new Set();
        linksByTab.forEach((link) => {
            if (link.type === "content") {
                contentIds.add(link.contentId);
            }
        });
        referencesByModule.forEach((module) => module.contentIds.forEach((contentId) => contentIds.add(contentId)));
        // Fill the contentReferences object for each id
        contentIds.forEach((contentId) => {
            result.contentReferences[contentId] = {
                tabIds: getKeysWhere(linksByTab, (link) => link.type === "content" && link.contentId === contentId),
                moduleIds: getKeysWhere(referencesByModule, (module) => module.contentIds.has(contentId)),
            };
        });
    }
    if (options.includeCollectionReferences) {
        // Build up a set of unique collection ids
        const collectionIds = new Set();
        linksByTab.forEach((link) => {
            if (link.type === "collection") {
                collectionIds.add(link.collectionId);
            }
        });
        referencesByModule.forEach((module) => module.collectionIds.forEach((collectionId) => collectionIds.add(collectionId)));
        // Fill the collectionReferences object for each id
        collectionIds.forEach((collectionId) => {
            result.collectionReferences[collectionId] = {
                tabIds: getKeysWhere(linksByTab, (link) => link.type === "collection" && link.collectionId === collectionId),
                moduleIds: getKeysWhere(referencesByModule, (module) => module.collectionIds.has(collectionId)),
            };
        });
    }
    return result;
}
/**
 * Helpers
 */
function isPageLink(value) {
    return ("type" in value &&
        value.type === "page" &&
        "pageId" in value &&
        typeof value.pageId === "number");
}
function isContentLink(value) {
    return ("type" in value &&
        (value.type === "content" ||
            value.type === "podcast" ||
            value.type === "videoSeries") &&
        "contentId" in value &&
        typeof value.contentId === "number");
}
function isCollectionLink(value) {
    return ("type" in value &&
        value.type === "collection" &&
        "collectionId" in value &&
        typeof value.collectionId === "number");
}
/**
 * Returns true if there's a possibility that data might contain a link.
 */
const mayContainLink = (data) => (0, is_object_1.default)(data) || Array.isArray(data);
/**
 * Extracts a list of references from unknown data.
 */
const getReferences = (data, options, result = { pageIds: new Set(), contentIds: new Set(), collectionIds: new Set() }) => {
    const isObj = (0, is_object_1.default)(data);
    if (options.includePageReferences && isObj && isPageLink(data)) {
        result.pageIds.add(data.pageId);
    }
    else if (options.includeContentReferences && isObj && isContentLink(data)) {
        result.contentIds.add(data.contentId);
    }
    else if (options.includeCollectionReferences &&
        isObj &&
        isCollectionLink(data)) {
        result.collectionIds.add(data.collectionId);
    }
    else if (isObj) {
        Object.values(data)
            .filter(mayContainLink)
            .flatMap((data) => getReferences(data, options, result));
    }
    else if (Array.isArray(data)) {
        data
            .filter(mayContainLink)
            .flatMap((data) => getReferences(data, options, result));
    }
    return result;
};
/**
 * Recursively compiles a list of all pages that can be reached from the
 * provided page. The return value includes the list of page IDs as well as the
 * depth at which each id was referenced (1 = from the provided page, 2 = from a
 * page referenced from the provided page, and so on). Returns by reference for
 * performance reasons.
 */
const getPageDepths = (pageId, pages, referencesByModule, results, visitedPageIds = [], previousDepth = 0) => {
    const page = pages.find((page) => page.id === pageId);
    // If we've already searched the current page then don't search it again
    // (otherwise we could end up stuck looping forever if two pages link to
    // each other).
    if (!page || visitedPageIds.indexOf(page.id) !== -1) {
        return;
    }
    // Increase the depth and add the current page to the list of visited pages.
    const depth = previousDepth + 1;
    visitedPageIds.push(page.id);
    // Walk through every page refererenced by every module on the current page,
    // adding each to the list of page reference depths and then recursively
    // searching each for more references.
    const pageIds = new Set();
    page.moduleIds.forEach((moduleId) => {
        const references = referencesByModule.get(moduleId);
        if (references) {
            references.pageIds.forEach((pageId) => pageIds.add(pageId));
        }
    });
    pageIds.forEach((pageId) => {
        const prevPageId = results.get(pageId);
        results.set(pageId, prevPageId ? Math.min(prevPageId, depth) : depth);
        getPageDepths(pageId, pages, referencesByModule, results, visitedPageIds, depth);
    });
};
/**
 * A helper that extracs an array of keys from an object based on whether the
 * value under each key matches a filter.
 */
const getKeysWhere = (map, matcher) => {
    const keys = [];
    map.forEach((value, key) => {
        if (matcher(value)) {
            keys.push(key);
        }
    });
    return keys;
};
